import type { UIMessage } from 'ai'
import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentProps, HTMLAttributes } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'

export type MessageProps = HTMLAttributes<HTMLDivElement> & {
  from: UIMessage['role']
}

export const Message = ({ className, from, ...props }: MessageProps) => (
  <div
    className={cn(
      'group flex w-full items-end justify-end gap-2 py-4 ps-2',
      from === 'user' ? 'is-user' : 'is-assistant flex-row-reverse justify-end',
      className,
    )}
    {...props}
  />
)

const messageContentVariants = cva(
  'is-user:dark flex flex-col gap-2 overflow-hidden rounded-lg text-sm',
  {
    variants: {
      variant: {
        contained: [
          'px-4 py-3',
          'group-[.is-user]:bg-blue-9 group-[.is-user]:text-white',
          'group-[.is-assistant]:bg-gray-2 group-[.is-assistant]:text-gray-10',
        ],
        flat: [
          'group-[.is-user]:bg-gray-2 group-[.is-user]:text-gray-10 group-[.is-user]:px-4 group-[.is-user]:py-3',
          'group-[.is-assistant]:text-gray-10 group-[.is-assistant]:max-w-full',
        ],
      },
      size: {
        default: ['group-[.is-user]:max-w-[80%]', 'group-[.is-assistant]:max-w-[80%]'],
        compact: ['group-[.is-user]:max-w-[90%]', 'group-[.is-assistant]:max-w-full'],
      },
    },
    defaultVariants: {
      variant: 'contained',
      size: 'default',
    },
  },
)

export type MessageContentProps = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof messageContentVariants>

export const MessageContent = ({
  children,
  className,
  size,
  variant,
  ...props
}: MessageContentProps) => (
  <div className={cn(messageContentVariants({ size, variant, className }))} {...props}>
    {children}
  </div>
)

export type MessageAvatarProps = ComponentProps<typeof Avatar> & {
  src: string
  name?: string
}

export const MessageAvatar = ({ src, name, className, ...props }: MessageAvatarProps) => (
  <Avatar className={cn('size-8 ring-1 ring-border', className)} {...props}>
    <AvatarImage alt="" className="my-0" src={src} />
    <AvatarFallback>{name?.slice(0, 2) || 'ME'}</AvatarFallback>
  </Avatar>
)
